import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

export const getApiErrorMessage = (error: unknown): string => {
    if (!error) return "";

    // If it's already a string, return it
    if (typeof error === "string") return error;

    if (typeof error === "object" && error !== null) {
        const err = error as any;

        // 1. Handle FetchBaseQueryError (standard RTK Query error with .data)
        if (err.data) {
            const data = err.data;

            // Handle the specific format: { error: { "field": ["msg"] } }
            if (data.error && typeof data.error === "object" && !Array.isArray(data.error)) {
                try {
                    const messages = Object.values(data.error).flatMap((val: any) => {
                        if (Array.isArray(val)) return val.map((v) => String(v));
                        return [String(val)];
                    });
                    if (messages.length > 0) return [...new Set(messages)].join(" | ");
                } catch (e) {
                    console.error("Error parsing validation error:", e);
                }
            }

            // Alternative format: { errors: { "field": ["msg"] } }
            if (data.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
                try {
                    const messages = Object.values(data.errors).flatMap((val: any) => {
                        if (Array.isArray(val)) return val.map((v) => String(v));
                        return [String(val)];
                    });
                    if (messages.length > 0) return [...new Set(messages)].join(" | ");
                } catch (e) {
                    console.error("Error parsing validation errors:", e);
                }
            }

            // Fallback for direct error string or message
            if (typeof data.error === "string") return data.error;
            if (data.message && typeof data.message === "string") return data.message;

            try {
                return JSON.stringify(data);
            } catch (e) {
                return "Server returned an error";
            }
        }

        // 2. Handle if the error object itself has an 'error' property
        if (err.error) {
            // If err.error is a string, return it
            if (typeof err.error === "string") return err.error;

            // If err.error is an object, it might be a nested FetchBaseQueryError or SerializedError
            if (typeof err.error === "object" && !Array.isArray(err.error)) {
                // If it has a 'data' property, it's likely a nested FetchBaseQueryError
                if (err.error.data) {
                    return getApiErrorMessage(err.error);
                }

                // If it has a 'message' property, it might be a SerializedError
                if (err.error.message) {
                    return String(err.error.message);
                }

                // If it's just an object, try to extract values except for common system properties
                try {
                    const messages = Object.entries(err.error)
                        .filter(([key]) => key !== 'status' && key !== 'name')
                        .map(([_, val]) => {
                            if (typeof val === 'object') return JSON.stringify(val);
                            return String(val);
                        });
                    if (messages.length > 0) return messages.join(" | ");
                } catch (e) {
                    // fall through
                }
            }
        }

        // 3. Handle SerializedError or standard Error objects
        if (err.message) return String(err.message);

        try {
            const str = JSON.stringify(err);
            return str === "{}" ? "An unknown error object was received" : str;
        } catch (e) {
            return "An un-stringifiable error occurred";
        }
    }

    return String(error) || "An unknown error occurred";
}; 
